import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-aria-components';
import { Plural, Trans, useLingui } from '@lingui/react/macro';
import { parseISO } from 'date-fns';

import Loader from '@/components/Loader';
import config from '@/config';
import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconExternalLink from '@/icons/ExternalLink.svg';
import IconPrint from '@/icons/Print.svg';
import { ArticleWithRelations, RequestWithRelations } from '@/models/request';
import {
  SHIPMENT_CARRIERS,
  ShipmentWithRelations,
  useShipments,
  useShipShipment,
} from '@/models/shipment';
import { useClientToken, useCurrentOrganization, useStoreToken } from '@/services/auth';
import { formatDate } from '@/utils/date';
import useViewPort from '@/utils/useViewport';

import { StepMessage } from './StepMessage';

export const PendingShipmentPreparation = ({
  request,
  articlesInTransit,
}: {
  request: RequestWithRelations;
  articlesInTransit: ArticleWithRelations[];
}) => {
  const { t } = useLingui();
  const { isMobile } = useViewPort();
  const clientToken = useClientToken();
  const storeToken = useStoreToken();
  const [organization] = useCurrentOrganization();
  const afterSalesServiceEmail = organization?.config.afterSalesServiceEmail;

  const ref = useRef<HTMLButtonElement>(null);
  const [showError, setShowError] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The article step is saved as `transit` before the shipment exists: carrier
  // selection runs in between. A fetch done in that window comes back empty and
  // would stay empty until a manual reload. Keep asking until the shipment is there.
  const { data: { shipments } = { shipments: [] }, isLoading: isLoadingShipment } = useShipments(
    {
      requestId: request.id,
    },
    {
      refetchInterval: (query) => {
        if (!clientToken) {
          return false;
        }

        const hasShipment = !!query.state.data?.shipments.length;

        return hasShipment ? false : 1000;
      },
    }
  );

  const shipment = shipments[0] as ShipmentWithRelations | undefined;
  const shipmentCarrier = SHIPMENT_CARRIERS.find((carrier) => carrier.id === shipment?.carrier);
  const shipmentCarrierName = shipmentCarrier?.name;

  const {
    mutateAsync: shipShipment,
    isPending: isPendingShipShipment,
    isSuccess: isSuccessShipShipment,
    reset,
  } = useShipShipment();

  useEffect(() => {
    // Automatically download the shipping label if needed
    if (isSuccessShipShipment && !!shipment?.shippingDeclaredAt) {
      ref.current?.click();
      reset();
    }
  }, [isSuccessShipShipment, shipment?.shippingDeclaredAt, ref, reset]);

  if (!shipment) {
    if (!clientToken) {
      return null;
    }

    return (
      <Stack gap="1rem" alignItems="center">
        <Loader style={{ height: '40px', width: '40px' }} />
        <Paragraph>
          <Trans id="_general.loading">Loading...</Trans>
        </Paragraph>
      </Stack>
    );
  }

  const pickupDate = shipment?.pickupDate
    ? formatDate(parseISO(shipment.pickupDate), {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })
    : undefined;

  return (
    <Stack gap="1rem">
      {shipment.handover === 'dropoff' && (
        <StepMessage
          message={
            <Trans id="client.request.transit.pending.home.title.dropoff">
              Please prepare the package and take it to a drop-off point.
            </Trans>
          }
        />
      )}

      {shipment.handover === 'pickup' && (
        <StepMessage
          message={
            <Trans id="client.request.transit.pending.home.title.pickup">
              Please prepare the package and wait for the carrier to pick it up.
            </Trans>
          }
        />
      )}

      {!!pickupDate && (
        <StepMessage
          message={
            <Trans id="store.request.transit.pending.pickup-date">
              The package will be picked up on {pickupDate}.
            </Trans>
          }
        />
      )}
      <Box gap="1rem">
        <Stack row gap="1rem" flexWrap="nowrap" alignItems="center">
          {!isMobile && (
            <img
              src="/shipment-label.png"
              alt=""
              style={{ borderRadius: '4px', width: '7rem', height: '7rem', objectFit: 'cover' }}
            />
          )}
          <Stack gap="0.5rem" alignItems="flex-start">
            <ol style={{ paddingLeft: '1rem' }}>
              <li>
                <Paragraph>
                  <Trans id="client.request.transit.pending.home.li-1">
                    Print the{' '}
                    <Plural
                      value={articlesInTransit.length}
                      one="item tracking sheet"
                      other="item tracking sheets"
                    />{' '}
                    and shipping label
                  </Trans>
                </Paragraph>
              </li>
              <li>
                <Paragraph>
                  <Trans id="client.request.transit.pending.home.li-2">
                    Put the{' '}
                    <Plural
                      value={articlesInTransit.length}
                      one="item tracking sheet"
                      other="item tracking sheets"
                    />{' '}
                    inside the package and stick the shipping label on the package
                  </Trans>
                </Paragraph>
              </li>
              <li>
                {shipment.handover === 'dropoff' && (
                  <Paragraph>
                    <Trans id="client.request.transit.pending.home.li-3.dropoff">
                      Take the package to a {shipmentCarrierName} drop-off point
                    </Trans>
                  </Paragraph>
                )}
                {shipment.handover === 'pickup' && (
                  <Paragraph>
                    <Trans id="client.request.transit.pending.home.li-3.pickup">
                      Wait for the carrier to pick up the package at your address
                    </Trans>
                  </Paragraph>
                )}
              </li>
            </ol>
            {shipment.handover === 'dropoff' && !!shipmentCarrier?.dropoffPoints && (
              <Button variant="link" target="_blank" href={shipmentCarrier.dropoffPoints}>
                <Trans id="client.request.transit.pending.home.drop-off-points">
                  View drop-off points
                </Trans>
                <IconExternalLink />
              </Button>
            )}
          </Stack>
        </Stack>

        <Stack row={!isMobile} gap="1rem">
          <Button
            ref={ref}
            variant="brand"
            size="large"
            disabled={!shipment || isLoadingShipment || isPendingShipShipment}
            href={
              shipment.shippingDeclaredAt
                ? clientToken
                  ? `${config.apiUrl}/shipments/${shipment.id}/media?shippingLabel=1&itemsSheets=1&clientToken=${clientToken}`
                  : `${config.apiUrl}/shipments/${shipment.id}/media?shippingLabel=1&itemsSheets=1&storeToken=${storeToken}`
                : undefined
            }
            download
            onPress={() => {
              if (!shipment?.shippingDeclaredAt && !!shipment) {
                setShowError(false);
                shipShipment({ id: shipment.id }).catch((err) => {
                  if (err.message?.includes('Invalid shipping address')) {
                    setError(
                      t({
                        id: 'shipment.client-address-error',
                        message: 'Invalid shipping address, please review it.',
                      })
                    );
                  } else if (err.status === 502 || err.status === 400) {
                    setError(err.message as string);
                  }

                  setShowError(true);
                });
              }
            }}
            style={{ flex: 1 }}
          >
            <IconPrint />
            <Trans id="client.request.transit.pending.home.action.shipping-sheets">
              Print shipping sheets
            </Trans>
          </Button>
        </Stack>

        {showError && (
          <InlineText color="danger">
            {error ? (
              <>
                {afterSalesServiceEmail ? (
                  <Trans id="client.request.transit.error.support-email.with-error">
                    {error} - If the error persists, please contact support at{' '}
                    <Link
                      className="color-tertiary-700"
                      href={`mailto:${afterSalesServiceEmail}`}
                      target="_blank"
                    >
                      {afterSalesServiceEmail}
                    </Link>
                  </Trans>
                ) : (
                  <Trans id="client.request.transit.error.support.with-error">
                    {error} - If the error persists, please contact support
                  </Trans>
                )}
              </>
            ) : (
              <>
                {afterSalesServiceEmail ? (
                  <Trans id="client.request.transit.error.support-email">
                    An error occurred, please contact support at{' '}
                    <Link
                      className="color-tertiary-700"
                      href={`mailto:${afterSalesServiceEmail}`}
                      target="_blank"
                    >
                      {afterSalesServiceEmail}
                    </Link>
                  </Trans>
                ) : (
                  <Trans id="client.request.transit.error.support">
                    An error occurred, please contact support
                  </Trans>
                )}
              </>
            )}
          </InlineText>
        )}
      </Box>
    </Stack>
  );
};
